import { useState, useEffect, useCallback, useRef } from 'react'

interface EditLock {
  id: string
  post_id: string
  locked_by: string
  locked_at: string
  expires_at: string
  lock_token: string
  user_email?: string
}

interface ApiService {
  blog: {
    locks: {
      check: (postId: string) => Promise<EditLock | null>
      acquire: (postId: string) => Promise<{ lock_token: string; success?: boolean; lock?: EditLock }>
      release: (postId: string, lockToken: string) => Promise<void>
    }
  }
}

// Hook for managing edit locks
export const useEditLock = (postId: string, apiService: ApiService) => {
  const [lock, setLock] = useState<EditLock | null>(null)
  const [lockToken, setLockToken] = useState<string | null>(null)
  const [isAcquiring, setIsAcquiring] = useState(false)
  const lockRef = useRef<EditLock | null>(null)

  useEffect(() => {
    lockRef.current = lock
  }, [lock])

  const checkLock = useCallback(async () => {
    try {
      const lockData = await apiService.blog.locks.check(postId)
      setLock(lockData)
      return lockData
    } catch (error) {
      console.error('Failed to check lock:', error)
      return null
    }
  }, [postId, apiService])

  const acquireLock = useCallback(async () => {
    setIsAcquiring(true)
    try {
      const response = await apiService.blog.locks.acquire(postId)
      if (response.lock_token) {
        const lockData = await apiService.blog.locks.check(postId)
        setLock(lockData)
        setLockToken(response.lock_token)
        return lockData
      }
      return null
    } catch (error) {
      console.error('Failed to acquire lock:', error)
      return null
    } finally {
      setIsAcquiring(false)
    }
  }, [postId, apiService])

  const releaseLock = useCallback(async () => {
    if (!lockToken) return

    try {
      await apiService.blog.locks.release(postId, lockToken)
      setLock(null)
      setLockToken(null)
      return true
    } catch (error) {
      console.error('Failed to release lock:', error)
      return false
    }
  }, [lockToken, postId, apiService])

  const renewLock = useCallback(async () => {
    if (!lockRef.current) return

    try {
      const response = await apiService.blog.locks.acquire(postId)
      if (response.success) {
        setLock(response.lock || null)
        return response.lock
      }
      return null
    } catch (error) {
      console.error('Failed to renew lock:', error)
      return null
    }
  }, [postId, apiService])

  useEffect(() => {
    if (!lockToken || !lockRef.current) return

    const renewInterval = setInterval(() => {
      if (!lockRef.current) return
      apiService.blog.locks.acquire(postId).then(response => {
        if (response.success) {
          setLock(response.lock || null)
        }
      }).catch(err => {
        console.error('Failed to renew lock:', err)
      })
    }, 4 * 60 * 1000)

    return () => clearInterval(renewInterval)
  }, [lockToken, postId, apiService])

  useEffect(() => {
    return () => {
      if (lockToken) {
        apiService.blog.locks.release(postId, lockToken).catch(err => {
          console.error('Failed to release lock on unmount:', err)
        })
      }
    }
  }, [lockToken, postId, apiService])

  return {
    lock,
    lockToken,
    isAcquiring,
    checkLock,
    acquireLock,
    releaseLock,
    renewLock,
  }
}